import React, { useState, useEffect } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Card, CardBody, CardHeader, Col, Row, Badge } from 'reactstrap';
import { H5, H6 } from '../../../AbstractElements';
import { getUsers } from '../../../api/userCheckIn';
import { UserCheck, UserX, Phone, Home } from 'react-feather';

const UserDistributionChart = () => {
  const [distribution, setDistribution] = useState({
    followed: 0,
    notFollowed: 0,
    withPhone: 0,
    withPharmacy: 0
  });

  const [chartData, setChartData] = useState({
    series: [50, 50],
    options: {
      chart: {
        type: 'donut',
        height: 280
      },
      labels: ['Followed OA', 'Not Followed'],
      colors: ['#54BA4A', '#FFA941'],
      legend: {
        show: false
      },
      dataLabels: {
        enabled: true,
        formatter: function (val) {
          return Math.round(val) + '%';
        }
      },
      plotOptions: {
        pie: {
          donut: {
            size: '70%',
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: '16px',
                fontWeight: 600
              },
              value: {
                show: true,
                fontSize: '24px',
                fontWeight: 700,
                formatter: function (val) {
                  return val;
                }
              },
              total: {
                show: true,
                label: 'Total Users',
                fontSize: '14px',
                fontWeight: 400,
                formatter: function (w) {
                  return w.globals.seriesTotals.reduce((a, b) => {
                    return a + b;
                  }, 0);
                }
              }
            }
          }
        }
      }
    }
  });

  useEffect(() => {
    fetchUserDistribution();
  }, []);

  const fetchUserDistribution = async () => {
    try {
      const response = await getUsers({ page: 1, limit: 100 });
      
      let userData = [];
      if (response?.data?.users && Array.isArray(response.data.users)) {
        userData = response.data.users;
      } else if (response?.data && Array.isArray(response.data)) {
        userData = response.data;
      } else if (response?.users && Array.isArray(response.users)) {
        userData = response.users;
      } else if (Array.isArray(response)) {
        userData = response;
      }
      
      // Get total count from pagination if available
      const totalUsers = response?.data?.pagination?.total || userData.length;
      const followed = userData.filter(user => user.followedOA).length;
      const notFollowed = userData.length - followed;
      const withPhone = userData.filter(user => user.phone).length;
      const withPharmacy = userData.filter(user => user.pharmacyName).length;
      
      // Calculate percentages for current batch and scale to total
      const followedPercentage = userData.length > 0 ? (followed / userData.length) : 0;
      const pharmacyPercentage = userData.length > 0 ? (withPharmacy / userData.length) : 0;
      const phonePercentage = userData.length > 0 ? (withPhone / userData.length) : 0;
      
      setDistribution({
        followed: Math.round(totalUsers * followedPercentage),
        notFollowed: totalUsers - Math.round(totalUsers * followedPercentage),
        withPhone: Math.round(totalUsers * phonePercentage),
        withPharmacy: Math.round(totalUsers * pharmacyPercentage)
      });

      setChartData(prev => ({
        ...prev,
        series: [Math.round(totalUsers * followedPercentage), totalUsers - Math.round(totalUsers * followedPercentage)]
      }));
    } catch (error) {
      console.error('Error fetching user distribution:', error);
    }
  };

  const distributionData = [
    {
      icon: UserCheck,
      title: 'Followed OA',
      value: distribution.followed,
      color: 'success',
      bgColor: 'light-success'
    },
    {
      icon: UserX,
      title: 'Not Followed',
      value: distribution.notFollowed,
      color: 'warning',
      bgColor: 'light-warning'
    },
    {
      icon: Phone,
      title: 'Has Phone',
      value: distribution.withPhone,
      color: 'primary',
      bgColor: 'light-primary'
    },
    {
      icon: Home,
      title: 'Has Pharmacy',
      value: distribution.withPharmacy,
      color: 'info',
      bgColor: 'light-info'
    }
  ];

  return (
    <Card className='portfolio-card'>
      <CardHeader className='card-no-border'>
        <div className='header-top'>
          <H5 className='m-0'>User Distribution</H5>
        </div>
      </CardHeader>
      <CardBody>
        <Row>
          <Col xxl='12' xl='7' sm='6' className='box-col-6'>
            <div className='portfolio-chart-container'>
              <ReactApexChart 
                type='donut' 
                height={280} 
                options={chartData.options} 
                series={chartData.series} 
              />
            </div>
          </Col>
          <Col xxl='12' xl='5' sm='6' className='box-col-6'>
            <div className='portfolio-table'>
              <Row className='g-3'>
                {distributionData.map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <Col key={i} xs='12'>
                      <div className={`d-flex align-items-center gap-3 p-3 rounded bg-${item.bgColor}`}>
                        <div className={`bg-${item.color} p-2 rounded`}>
                          <Icon size={20} className='text-white' />
                        </div>
                        <div className='flex-grow-1'>
                          <span className='f-light f-14'>{item.title}</span>
                          <H6 attrH6={{ className: 'f-16 f-w-600 mb-0 mt-1' }}>
                            {item.value}
                          </H6>
                        </div>
                        <Badge color={item.color}>
                          {distribution.followed + distribution.notFollowed > 0
                            ? Math.round((item.value / (distribution.followed + distribution.notFollowed)) * 100)
                            : 0}%
                        </Badge>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </div>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

export default UserDistributionChart;

